import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Table, Pagination, Button, InputGroup, Modal, Nav, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';
import { ref, onValue, remove, set, push, get, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Car } from '../types/car';
import { Link, useLocation } from 'react-router-dom';

interface Log {
  id: string;
  timestamp: number;
  what: string;
  who: string;
  odometer: number;
  price: number;
  file?: string;
  file_url?: string;
  file_path?: string;
  isEditing?: boolean;
}

interface EditingLog {
  what: string;
  who: string;
  odometer: string;
  price: string;
}

interface FileViewerProps {
  show: boolean;
  onHide: () => void;
  fileUrl: string;
  fileName: string;
}

type SortField = 'timestamp' | 'odometer' | 'price' | 'what' | 'who';
type SortDirection = 'asc' | 'desc';

const FileViewer: React.FC<FileViewerProps> = ({ show, onHide, fileUrl, fileName }) => {
  const fileType = fileName.split('.').pop()?.toLowerCase();
  const isPdf = fileType === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png'].includes(fileType || '');

  // Calculate maximum height for the modal body
  const [maxHeight, setMaxHeight] = useState('80vh');
  
  useEffect(() => {
    if (show) {
      // Subtract header height and padding to get available space
      const windowHeight = window.innerHeight;
      const headerHeight = 56; // Bootstrap modal header height
      const padding = 32; // Total vertical padding
      const availableHeight = windowHeight - headerHeight - padding;
      setMaxHeight(`${availableHeight}px`);
    }
  }, [show]);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      dialogClassName="modal-90w"
      contentClassName="h-100"
    >
      <Modal.Header closeButton>
        <Modal.Title>{fileName}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3" style={{ maxHeight }}>
        <div className="bg-light p-3 rounded h-100">
          {isPdf && (
            <iframe
              src={`${fileUrl}#toolbar=0`}
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: '75vh' }}
              title={fileName}
            />
          )}
          {isImage && (
            <div className="d-flex align-items-center justify-content-center h-100">
              <img
                src={fileUrl}
                alt={fileName}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain'
                }}
              />
            </div>
          )}
          {!isPdf && !isImage && (
            <div className="p-4">
              <p>Diese Datei kann nicht angezeigt werden.</p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Datei herunterladen
              </a>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// Add custom CSS for the 90% width modal
const modalStyle = document.createElement('style');
modalStyle.innerHTML = `
  .modal-90w {
    width: 90vw;
    max-width: 90vw !important;
  }
`;
document.head.appendChild(modalStyle);

const MoschtLogs = () => {
  const { currentUser } = useAuth();
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [logs, setLogs] = useState<Log[]>([]);
  const [editingLog, setEditingLog] = useState<EditingLog>({
    what: '',
    who: '',
    odometer: '',
    price: ''
  });
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const itemsPerPage = 20;
  const [newLog, setNewLog] = useState({
    odometer: '',
    what: '',
    who: '',
    price: ''
  });
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'danger', message: string} | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch owned car IDs
    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    // Fetch all cars data
    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});
    });

    // Fetch selected car
    const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
    const selectedCarUnsubscribe = onValue(selectedCarRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedCar(data || '');
    });

    return () => {
      ownedCarsUnsubscribe();
      carsUnsubscribe();
      selectedCarUnsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedCar) {
      setLogs([]);
      return;
    }

    const logsRef = ref(database, `cars/${selectedCar}/logs`);
    const logsUnsubscribe = onValue(logsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setLogs([]);
        return;
      }

      const logsList = Object.entries(data).map(([id, log]: [string, any]) => ({
        id,
        ...log,
        timestamp: Number(log.timestamp),
        odometer: Number(log.odometer),
        price: Number(log.price),
        file: log.file || undefined,
        file_url: log.file_url || undefined,
        file_path: log.file_path || undefined
      }));

      setLogs(logsList);
    });

    return () => logsUnsubscribe();
  }, [currentUser, selectedCar]);

  const handleCarSelect = async (carId: string) => {
    if (!currentUser) return;
    
    try {
      const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
      await set(selectedCarRef, carId);
      setSelectedCar(carId);
    } catch (error) {
      console.error('Error setting selected car:', error);
    }
  };

  const handleRemoveLog = async (logId: string) => {
    if (!currentUser || !selectedCar) return;
    
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      return;
    }

    try {
      // Get the log data first to check for file
      const logRef = ref(database, `cars/${selectedCar}/logs/${logId}`);
      const logSnapshot = await get(logRef);
      const logData = logSnapshot.val();

      // Delete file from storage if it exists
      if (logData?.file_path) {
        try {
          const fileRef = storageRef(storage, logData.file_path);
          await deleteObject(fileRef);
        } catch (error) {
          console.error('Error deleting file:', error);
          // Continue with log deletion even if file deletion fails
        }
      }

      // Delete log entry from database
      await remove(logRef);
      setAlertMessage({
        type: 'success',
        message: 'Eintrag erfolgreich gelöscht!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error removing log:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Löschen des Eintrags. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const resetForm = () => {
    setNewLog({
      odometer: '',
      what: '',
      who: '',
      price: ''
    });
    setSelectedFile(null);
    // Reset file input by clearing its value
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    if (!currentUser || !selectedCar) return;

    try {
      setIsUploading(true);
      let fileData = { name: '', url: '', path: '' };

      if (selectedFile) {
        // Upload file to Firebase Storage
        const timestamp = Date.now();
        const fileExtension = selectedFile.name.split('.').pop();
        const fileName = `${timestamp}.${fileExtension}`;
        const filePath = `cars/${selectedCar}/logs/${fileName}`;
        const fileRef = storageRef(storage, filePath);
        
        await uploadBytes(fileRef, selectedFile);
        const downloadUrl = await getDownloadURL(fileRef);
        
        fileData = {
          name: selectedFile.name,
          url: downloadUrl,
          path: filePath
        };
      }

      // Create log entry in database
      const logsRef = ref(database, `cars/${selectedCar}/logs`);
      await push(logsRef, {
        timestamp: Date.now(),
        odometer: Number(newLog.odometer),
        what: newLog.what,
        who: newLog.who,
        price: Number(newLog.price),
        file: fileData.name || undefined,
        file_url: fileData.url || undefined,
        file_path: fileData.path || undefined
      });
      
      // Reset form and close modal
      resetForm();
      setShowAddModal(false);
      setAlertMessage({
        type: 'success',
        message: 'Wartungseintrag erfolgreich hinzugefügt!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);

    } catch (error) {
      console.error('Error creating new log:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Hochladen des Belegs. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditStart = (log: Log) => {
    setEditingLog({
      what: log.what,
      who: log.who,
      odometer: log.odometer.toString(),
      price: log.price.toString()
    });
    setLogs(logs.map(l => ({
      ...l,
      isEditing: l.id === log.id
    })));
  };

  const handleEditCancel = () => {
    setLogs(logs.map(l => ({
      ...l,
      isEditing: false
    })));
  };

  const handleEditSave = async (log: Log) => {
    if (!currentUser || !selectedCar) return;

    try {
      const logRef = ref(database, `cars/${selectedCar}/logs/${log.id}`);
      await update(logRef, {
        what: editingLog.what,
        who: editingLog.who,
        odometer: Number(editingLog.odometer),
        price: Number(editingLog.price)
      });

      setLogs(logs.map(l => ({
        ...l,
        isEditing: false
      })));
      
      setAlertMessage({
        type: 'success',
        message: 'Eintrag erfolgreich aktualisiert!'
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating log:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Aktualisieren des Eintrags. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const handleFileClick = (e: React.MouseEvent<HTMLAnchorElement>, fileUrl: string, fileName: string) => {
    e.preventDefault();
    setSelectedFileUrl(fileUrl);
    setSelectedFileName(fileName);
    setShowFileViewer(true);
  };

  const sortedLogs = [...logs].sort((a, b) => {
    if (sortField === 'timestamp' || sortField === 'odometer' || sortField === 'price') {
      return sortDirection === 'asc' 
        ? a[sortField] - b[sortField]
        : b[sortField] - a[sortField];
    }
    return sortDirection === 'asc'
      ? a[sortField].localeCompare(b[sortField])
      : b[sortField].localeCompare(a[sortField]);
  });

  const indexOfLastLog = currentPage * itemsPerPage;
  const indexOfFirstLog = indexOfLastLog - itemsPerPage;
  const currentLogs = sortedLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(sortedLogs.length / itemsPerPage);

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  const renderSortArrow = (field: SortField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <Container>
      <h2 className="mb-4">Moscht Logs</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht" 
            active={location.pathname === '/dashboard/moscht'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/tankungen" 
            active={location.pathname === '/dashboard/moscht/tankungen'}
          >
            Tankungen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/events" 
            active={location.pathname === '/dashboard/moscht/events'}
          >
            Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/logs" 
            active={location.pathname === '/dashboard/moscht/logs'}
          >
            Logs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/cars" 
            active={location.pathname === '/dashboard/moscht/cars'}
          >
            Fahrzeuge
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/settings" 
            active={location.pathname === '/dashboard/moscht/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      {alertMessage && (
        <Alert variant={alertMessage.type} onClose={() => setAlertMessage(null)} dismissible>
          {alertMessage.message}
        </Alert>
      )}
      
      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Fahrzeugauswahl</Card.Title>
          <Form className="mt-3">
            <Form.Group>
              <Form.Label>Aktuelles Fahrzeug</Form.Label>
              <Form.Select 
                value={selectedCar} 
                onChange={(e) => handleCarSelect(e.target.value)}
                className="mb-3"
              >
                <option value="">Fahrzeug auswählen...</option>
                {ownedCarIds
                  .filter(id => cars[id] && cars[id].is_active)
                  .map(id => ({
                    id,
                    ...cars[id]
                  }))
                  .map(car => (
                    <option key={car.id} value={car.id}>
                      {car.name}
                    </option>
                  ))}
              </Form.Select>
              {!ownedCarIds.length && (
                <div className="text-muted">
                  Keine aktiven Fahrzeuge verfügbar. Bitte fügen Sie zuerst Fahrzeuge in Ihrem Profil hinzu.
                </div>
              )}
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      {selectedCar && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Card.Title style={{ color: '#1780a6' }}>Wartungslog</Card.Title>
              <Button 
                variant="primary" 
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
              >
                Neue Wartung hinzufügen
              </Button>
            </div>
            
            {logs.length > 0 ? (
              <>
                <Table responsive className="mt-3">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                        Datum {renderSortArrow('timestamp')}
                      </th>
                      <th onClick={() => handleSort('what')} style={{ cursor: 'pointer' }}>
                        Was {renderSortArrow('what')}
                      </th>
                      <th onClick={() => handleSort('who')} style={{ cursor: 'pointer' }}>
                        Wer {renderSortArrow('who')}
                      </th>
                      <th onClick={() => handleSort('odometer')} style={{ cursor: 'pointer' }}>
                        Kilometerstand {renderSortArrow('odometer')}
                      </th>
                      <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                        Kosten {renderSortArrow('price')}
                      </th>
                      <th>Beleg</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLogs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.timestamp).toLocaleDateString()}</td>
                        <td>
                          {log.isEditing ? (
                            <Form.Control
                              type="text"
                              value={editingLog.what}
                              onChange={(e) => setEditingLog({ ...editingLog, what: e.target.value })}
                              required
                            />
                          ) : (
                            log.what
                          )}
                        </td>
                        <td>
                          {log.isEditing ? (
                            <Form.Control
                              type="text"
                              value={editingLog.who}
                              onChange={(e) => setEditingLog({ ...editingLog, who: e.target.value })}
                              required
                            />
                          ) : (
                            log.who
                          )}
                        </td>
                        <td>
                          {log.isEditing ? (
                            <InputGroup>
                              <Form.Control
                                type="number"
                                value={editingLog.odometer}
                                onChange={(e) => setEditingLog({ ...editingLog, odometer: e.target.value })}
                                min="0"
                                required
                              />
                              <InputGroup.Text>km</InputGroup.Text>
                            </InputGroup>
                          ) : (
                            `${log.odometer.toLocaleString()} km`
                          )}
                        </td>
                        <td>
                          {log.isEditing ? (
                            <InputGroup>
                              <Form.Control
                                type="number"
                                value={editingLog.price}
                                onChange={(e) => setEditingLog({ ...editingLog, price: e.target.value })}
                                min="0"
                                step="0.01"
                                required
                              />
                              <InputGroup.Text>CHF</InputGroup.Text>
                            </InputGroup>
                          ) : (
                            `CHF ${log.price.toFixed(2)}`
                          )}
                        </td>
                        <td>
                          {log.file && log.file_url && (
                            <a 
                              href="#"
                              onClick={(e) => log.file && log.file_url && handleFileClick(e, log.file_url, log.file)}
                              style={{ cursor: 'pointer' }}
                              title={log.file}
                            >
                              Beleg
                            </a>
                          )}
                        </td>
                        <td>
                          {log.isEditing ? (
                            <>
                              <Button
                                variant="success"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditSave(log)}
                              >
                                Speichern
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleEditCancel}
                              >
                                Abbrechen
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditStart(log)}
                              >
                                Bearbeiten
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveLog(log.id)}
                              >
                                Löschen
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    {paginationItems}
                    <Pagination.Next 
                      onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </>
            ) : (
              <div className="text-muted text-center mt-3">
                Keine Wartungseinträge vorhanden.
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Add Log Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neue Wartung hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Kilometerstand</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={newLog.odometer}
                  onChange={(e) => setNewLog({ ...newLog, odometer: e.target.value })}
                  placeholder="0"
                  min="0"
                  required
                />
                <InputGroup.Text>km</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Wer</Form.Label>
              <Form.Control
                type="text"
                value={newLog.who}
                onChange={(e) => setNewLog({ ...newLog, who: e.target.value })}
                placeholder="Name der Person, die die Wartung durchgeführt hat..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newLog.what}
                onChange={(e) => setNewLog({ ...newLog, what: e.target.value })}
                placeholder="Beschreiben Sie die durchgeführte Wartung..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kosten</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={newLog.price}
                  onChange={(e) => setNewLog({ ...newLog, price: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
                <InputGroup.Text>CHF</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beleg</Form.Label>
              <Form.Control
                type="file"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!e.target.files) return;
                  const file = e.target.files[0];
                  if (file) {
                    // Check file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      alert('Die Datei ist zu gross. Maximale Grösse ist 5MB.');
                      e.target.value = '';
                      return;
                    }
                    // Check file type
                    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
                    if (!allowedTypes.includes(file.type)) {
                      alert('Nur JPEG, PNG und PDF Dateien sind erlaubt.');
                      e.target.value = '';
                      return;
                    }
                    setSelectedFile(file);
                  }
                }}
                accept=".jpg,.jpeg,.png,.pdf"
                as="input"
              />
              <Form.Text className="text-muted">
                Erlaubte Dateitypen: JPEG, PNG, PDF. Maximale Grösse: 5MB
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={() => handleSubmit()}
            disabled={isUploading}
          >
            {isUploading ? 'Wird gespeichert...' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Viewer Modal */}
      <FileViewer
        show={showFileViewer}
        onHide={() => setShowFileViewer(false)}
        fileUrl={selectedFileUrl}
        fileName={selectedFileName}
      />
    </Container>
  );
};

export default MoschtLogs;
